import { getAppRouteByNode } from "@app/router/taskToAppRouteMap";
import type { AppTask, TaskTreeNode } from "./types";

export type GetNodeIdFn = (item: AppTask, path: string, isFolder: boolean, name: string) => string;

/** Полный путь задачи (`FolderName/Name`) для сравнения и дедупликации. */
export function getTaskFullPath(task: AppTask): string {
    return [task.FolderName.trim(), task.Name.trim()]
        .filter(Boolean)
        .join("/")
        .replace(/[\\/]+/g, "/");
}

const PATH_SEPARATOR_REGEX = /[\\/]/g;

function splitPathIntoParts(path: string): string[] {
    if (path.length === 0) {
        return [];
    }
    return path
        .split(PATH_SEPARATOR_REGEX)
        .map((part) => part.trim())
        .filter(Boolean);
}

function isFolderNode(node: TaskTreeNode): boolean {
    return (node.children?.length ?? 0) > 0;
}

/**
 * Преобразует плоский список задач в дерево с предвычисленными `route`.
 */
export function parseTasksToTree(
    rows: AppTask[],
    params: {
        getId?: GetNodeIdFn;
        disableFn?: (data: { item: AppTask }) => boolean;
    } = {},
): TaskTreeNode[] {
    const {
        getId = (item, path, isFolder) => `${path}${isFolder ? "[folder]" : ""}`,
        disableFn = ({ item }) => getAppRouteByNode(item.FolderName, item.Name) == null,
    } = params;

    const nodesById = new Map<string, TaskTreeNode>();
    const rootIds = new Set<string>();

    const ensureFolderNode = (path: string, name: string, dataRaw: AppTask): TaskTreeNode => {
        const idNode = getId(dataRaw, path, true, name);
        const existing = nodesById.get(idNode);
        if (existing != null) {
            return existing;
        }

        const node: TaskTreeNode = {
            id: idNode,
            name,
            title: path,
            data: {
                task: dataRaw,
                route: null,
            },
            children: [],
        };
        nodesById.set(idNode, node);
        return node;
    };

    rows.forEach((row) => {
        const folderParts = splitPathIntoParts(row.FolderName);
        let currentParentPath = "";
        let currentParentNode: TaskTreeNode | null = null;

        for (const folderName of folderParts) {
            const folderPath = currentParentPath.length > 0 ? `${currentParentPath}/${folderName}` : folderName;
            const folderNode = ensureFolderNode(folderPath, folderName, row);

            if (currentParentNode != null) {
                currentParentNode.children = currentParentNode.children ?? [];
                const existsInParent = currentParentNode.children.some((child) => child.id === folderNode.id);
                if (!existsInParent) {
                    currentParentNode.children.push(folderNode);
                }
            } else {
                rootIds.add(folderNode.id);
            }

            currentParentNode = folderNode;
            currentParentPath = folderPath;
        }

        const taskName = (row.Name ?? "(unnamed)").trim();
        const taskPath = currentParentPath.length > 0 ? `${currentParentPath}/${taskName}` : taskName;
        const taskId = getId(row, taskPath, false, taskName);
        const disabled = disableFn({ item: row });
        const route = getAppRouteByNode(row.FolderName, row.Name) ?? null;

        const taskNode: TaskTreeNode = {
            id: taskId,
            name: taskName,
            title: taskPath,
            data: {
                task: row,
                route,
            },
            disabled,
            children: [],
        };
        nodesById.set(taskId, taskNode);

        if (currentParentNode != null) {
            currentParentNode.children = currentParentNode.children ?? [];
            currentParentNode.children.push(taskNode);
        } else {
            rootIds.add(taskId);
        }
    });

    const rootNodes: TaskTreeNode[] = [];
    rootIds.forEach((id) => {
        const node = nodesById.get(id);
        if (node != null) {
            rootNodes.push(node);
        }
    });

    const sortNodes = (nodes: TaskTreeNode[]): void => {
        nodes.sort((a, b) => {
            const aIsFolder = isFolderNode(a);
            const bIsFolder = isFolderNode(b);
            if (aIsFolder !== bIsFolder) {
                return aIsFolder ? -1 : 1;
            }
            return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
        });

        nodes.forEach((node) => {
            const children = node.children ?? [];
            if (children.length > 0) {
                sortNodes(children);
            }
        });
    };

    sortNodes(rootNodes);
    return rootNodes;
}

/**
 * Скрывает листовые задачи без route и пустые папки после фильтрации.
 */
export function filterNodesWithoutRoute(nodes: TaskTreeNode[], hideWithoutRoute: boolean): TaskTreeNode[] {
    if (!hideWithoutRoute) {
        return nodes;
    }

    const filterNode = (node: TaskTreeNode): TaskTreeNode | null => {
        const originalHasChildren = (node.children?.length ?? 0) > 0;
        let filteredChildren: TaskTreeNode[] | undefined;

        if (originalHasChildren && node.children != null) {
            filteredChildren = node.children.map(filterNode).filter((n): n is TaskTreeNode => n !== null);
        }

        const isLeaf = !originalHasChildren;
        if (isLeaf && (node.data.route == null || node.data.route === "")) {
            return null;
        }

        if (originalHasChildren && (filteredChildren == null || filteredChildren.length === 0)) {
            return null;
        }

        return {
            ...node,
            children: originalHasChildren ? filteredChildren : node.children,
        };
    };

    return nodes.map(filterNode).filter((n): n is TaskTreeNode => n !== null);
}
